import React, { useState, useEffect } from 'react';
import { X, Shield, Settings, Cookie } from 'lucide-react';

const CookieConsentPro = ({ onAccept, onReject, privacyPolicyUrl = '/privacy' }) => {
  const [show, setShow] = useState(false);
  const [showSettings, setShowSettings] = useState(false);

  useEffect(() => {
    const consent = localStorage.getItem('cookie_consent');

    if (!consent) {
      setTimeout(() => setShow(true), 1000);
    } else if (consent === 'accepted') {
      onAccept && onAccept();
    }
  }, [onAccept]);

  const handleAccept = () => {
    localStorage.setItem('cookie_consent', 'accepted');
    localStorage.setItem('analytics_enabled', 'true');
    setShow(false);
    onAccept && onAccept();
  };

  const handleReject = () => {
    localStorage.setItem('cookie_consent', 'rejected');
    localStorage.setItem('analytics_enabled', 'false');
    setShow(false);
    onReject && onReject();
  };

  const handleEssentialOnly = () => {
    localStorage.setItem('cookie_consent', 'essential');
    localStorage.setItem('analytics_enabled', 'false');
    setShow(false);
    setShowSettings(false);
    onReject && onReject();
  };

  if (!show) return null;

  return (
    <>
      {/* Floating consent card */}
      <div className="fixed bottom-4 left-4 right-4 md:left-auto md:right-6 md:bottom-6 md:max-w-md z-50 animate-slideUp">
        <div className="bg-black/90 backdrop-blur-2xl text-white rounded-3xl border border-white/10 shadow-[0_24px_60px_-12px_rgba(0,0,0,0.7)] overflow-hidden">
          <div className="p-6">
            <div className="flex items-start gap-4 mb-4">
              <div className="w-11 h-11 bg-orange-500/15 border border-orange-500/20 rounded-2xl flex items-center justify-center flex-shrink-0">
                <Cookie className="w-5 h-5 text-orange-500" strokeWidth={1.75} />
              </div>
              <div>
                <h3 className="text-[15px] font-bold mb-1">Your Privacy Matters</h3>
                <p className="text-[13px] text-gray-400 leading-relaxed">
                  We use cookies to enhance your experience and analyze site traffic. Your data is safe with us.
                </p>
              </div>
            </div>

            <div className="flex items-center gap-2">
              <button
                onClick={handleAccept}
                className="flex-1 px-4 py-2.5 bg-orange-500 hover:bg-orange-600 text-white rounded-xl transition-all duration-300 text-sm font-bold shadow-[0_8px_20px_-6px_rgba(249,115,22,0.5)] active:scale-95"
              >
                Accept All
              </button>
              <button
                onClick={handleReject}
                className="flex-1 px-4 py-2.5 bg-white/10 hover:bg-white/15 border border-white/10 text-white rounded-xl transition-all duration-300 text-sm font-semibold active:scale-95"
              >
                Reject
              </button>
              <button
                onClick={() => setShowSettings(true)}
                className="p-2.5 bg-white/5 hover:bg-white/10 border border-white/10 text-gray-300 rounded-xl transition-all duration-300 active:scale-95"
                aria-label="Cookie settings"
              >
                <Settings className="w-[18px] h-[18px]" strokeWidth={1.75} />
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Settings Modal */}
      {showSettings && (
        <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-4 animate-fadeIn">
          <div className="bg-white rounded-3xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            {/* Header */}
            <div className="p-6 border-b border-gray-100 flex items-center justify-between sticky top-0 bg-white/95 backdrop-blur-xl z-10 rounded-t-3xl">
              <div>
                <h2 className="text-xl font-bold text-[#1d1d1f] tracking-tight">Cookie Preferences</h2>
                <p className="text-sm text-gray-500 mt-0.5">Manage your cookie settings</p>
              </div>
              <button
                onClick={() => setShowSettings(false)}
                className="p-2.5 bg-gray-100 hover:bg-gray-200 rounded-full transition-colors active:scale-90"
                aria-label="Close settings"
              >
                <X className="w-5 h-5 text-gray-500" />
              </button>
            </div>

            {/* Content */}
            <div className="p-6 space-y-4">
              {/* Essential Cookies */}
              <div className="bg-[#fbfbfd] rounded-2xl p-5 border border-gray-100">
                <div className="flex items-start justify-between mb-2 gap-4">
                  <div className="flex-1">
                    <h3 className="font-semibold text-[#1d1d1f] mb-1">Essential Cookies</h3>
                    <p className="text-sm text-gray-500">
                      Required for the website to function. Cannot be disabled.
                    </p>
                  </div>
                  <div className="px-3 py-1 bg-green-100 text-green-700 text-xs font-bold rounded-full flex-shrink-0">
                    Always On
                  </div>
                </div>
                <p className="text-xs text-gray-400">
                  These cookies are necessary for basic site functionality like cart, login, and security.
                </p>
              </div>

              {/* Analytics Cookies */}
              <div className="bg-[#fbfbfd] rounded-2xl p-5 border border-gray-100">
                <div className="flex items-start justify-between mb-2 gap-4">
                  <div className="flex-1">
                    <h3 className="font-semibold text-[#1d1d1f] mb-1">Analytics Cookies</h3>
                    <p className="text-sm text-gray-500">
                      Help us understand how visitors interact with our website.
                    </p>
                  </div>
                  <label className="relative inline-flex items-center cursor-pointer flex-shrink-0">
                    <input type="checkbox" defaultChecked className="sr-only peer" />
                    <div className="w-11 h-6 bg-gray-300 peer-focus:ring-4 peer-focus:ring-orange-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-orange-500"></div>
                  </label>
                </div>
                <p className="text-xs text-gray-400">
                  We use Google Analytics to track page views, popular products, and improve user experience.
                </p>
              </div>

              {/* Info Box */}
              <div className="bg-orange-50 border border-orange-100 rounded-2xl p-4 flex items-start gap-3">
                <Shield className="w-5 h-5 text-orange-500 flex-shrink-0 mt-0.5" strokeWidth={1.75} />
                <p className="text-sm text-orange-900/80">
                  <strong>Your privacy is important.</strong> We never sell your data.
                  Cookies help us make your experience better and understand what you like.
                </p>
              </div>
            </div>

            {/* Footer Buttons */}
            <div className="p-6 border-t border-gray-100 bg-[#fbfbfd] flex flex-col sm:flex-row gap-3 rounded-b-3xl">
              <button
                onClick={handleEssentialOnly}
                className="flex-1 px-6 py-3 bg-white border border-gray-200 text-gray-700 rounded-xl hover:bg-gray-50 transition-colors text-sm font-semibold active:scale-[0.98]"
              >
                Essential Only
              </button>
              <button
                onClick={handleAccept}
                className="flex-1 px-6 py-3 bg-orange-500 hover:bg-orange-600 text-white rounded-xl transition-all text-sm font-bold shadow-[0_8px_20px_-6px_rgba(249,115,22,0.4)] active:scale-[0.98]"
              >
                Save &amp; Accept
              </button>
            </div>
          </div>
        </div>
      )}

      <style>{`
        @keyframes slideUp {
          from {
            transform: translateY(40px);
            opacity: 0;
          }
          to {
            transform: translateY(0);
            opacity: 1;
          }
        }
        @keyframes fadeIn {
          from { opacity: 0; }
          to { opacity: 1; }
        }
        .animate-slideUp {
          animation: slideUp 0.5s cubic-bezier(0.16, 1, 0.3, 1);
        }
        .animate-fadeIn {
          animation: fadeIn 0.2s ease-out;
        }
      `}</style>
    </>
  );
};

export default CookieConsentPro;
